import { useEffect, useState } from "react";
import { applyConsent, loadStoredPrefs, storePrefs, type CookiePrefs } from "./gtm";

export const OPEN_COOKIE_SETTINGS_EVENT = "wyceniarka:cookie-settings";

function Toggle({
  checked,
  disabled,
  onChange,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange?: (v: boolean) => void;
}) {
  return (
    <label
      className={`relative inline-flex shrink-0 items-center ${
        disabled ? "opacity-50" : "cursor-pointer"
      }`}
    >
      <input
        type="checkbox"
        className="sr-only peer"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.checked)}
      />
      <div className="w-11 h-6 bg-gray-300 rounded-full peer-checked:bg-brand transition-colors after:content-[''] after:absolute after:left-0.5 after:top-0.5 after:w-5 after:h-5 after:bg-white after:rounded-full after:transition-transform peer-checked:after:translate-x-5" />
    </label>
  );
}

const CATEGORIES = [
  {
    key: "necessary" as const,
    name: "Niezbędne pliki cookie",
    desc: "Te pliki cookie są niezbędne do prawidłowego funkcjonowania strony internetowej. Umożliwiają podstawowe funkcje, takie jak nawigacja po stronach, dostęp do bezpiecznych obszarów i zapamiętywanie Twoich preferencji.",
  },
  {
    key: "analytics" as const,
    name: "Pliki cookie analityczne",
    desc: "Te pliki cookie pomagają nam zrozumieć, jak odwiedzający wchodzą w interakcję z naszą stroną internetową, zbierając i raportując informacje anonimowo. To pomaga nam poprawić wydajność naszej strony internetowej i doświadczenia użytkownika.",
  },
  {
    key: "ads" as const,
    name: "Pliki cookie reklamowe",
    desc: "Te pliki cookie są używane do dostarczania reklam bardziej odpowiednich dla Ciebie i Twoich zainteresowań. Mogą być również używane do ograniczenia liczby razy, gdy widzisz reklamę i pomiaru skuteczności kampanii reklamowych.",
  },
];

export default function CookieConsent() {
  const [banner, setBanner] = useState(false);
  const [modal, setModal] = useState(false);
  const [prefs, setPrefs] = useState<CookiePrefs>({ analytics: true, ads: true });

  useEffect(() => {
    const stored = loadStoredPrefs();
    if (stored) {
      applyConsent(stored);
    } else {
      setBanner(true);
    }

    const openSettings = () => {
      setPrefs(loadStoredPrefs() ?? { analytics: true, ads: true });
      setBanner(false);
      setModal(true);
    };
    window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, openSettings);
    return () => window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, openSettings);
  }, []);

  function finish(chosen: CookiePrefs) {
    storePrefs(chosen);
    applyConsent(chosen);
    setBanner(false);
    setModal(false);
  }

  function cancelModal() {
    setModal(false);
    if (!loadStoredPrefs()) setBanner(true);
  }

  if (!banner && !modal) return null;

  return (
    <>
      {banner && (
        <div className="fixed bottom-0 inset-x-0 z-50 bg-white border-t border-gray-200 shadow-2xl">
          <div className="max-w-3xl mx-auto px-6 py-4 space-y-3">
            <p className="text-sm text-gray-700">
              Używamy plików cookie, aby poprawić Twoje doświadczenia przeglądania,
              dostarczać spersonalizowane treści i analizować nasz ruch. Klikając
              „Zaakceptuj wszystkie”, wyrażasz zgodę na używanie przez nas plików cookie.{" "}
              <a
                href="/polityka-cookies"
                target="_blank"
                rel="noreferrer"
                className="underline"
              >
                Dowiedz się więcej o naszej Polityce plików cookie
              </a>
              .
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => finish({ analytics: false, ads: false })}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition"
              >
                Odrzuć wszystkie
              </button>
              <button
                type="button"
                onClick={() => {
                  setBanner(false);
                  setModal(true);
                }}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition"
              >
                Preferencje plików cookie
              </button>
              <button
                type="button"
                onClick={() => finish({ analytics: true, ads: true })}
                className="px-4 py-2 text-sm rounded-lg bg-brand text-white font-semibold hover:bg-brand-600 transition"
              >
                Zaakceptuj wszystkie
              </button>
            </div>
          </div>
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="w-full max-w-lg max-h-[85vh] overflow-y-auto bg-white rounded-2xl shadow-2xl p-6">
            <h2 className="text-xl font-bold text-brand">Preferencje plików cookie</h2>
            <p className="mt-2 text-sm text-gray-600">
              Szanujemy Twoją prywatność. Możesz wybrać, które typy plików cookie chcesz
              zezwolić. Twoje preferencje będą zapisane i zastosowane na całej naszej
              stronie internetowej.
            </p>

            <div className="mt-5 space-y-5">
              {CATEGORIES.map((cat) => (
                <div key={cat.key} className="flex items-start justify-between gap-4">
                  <div>
                    <div className="font-medium text-gray-900">{cat.name}</div>
                    <p className="mt-1 text-sm text-gray-600">{cat.desc}</p>
                  </div>
                  {cat.key === "necessary" ? (
                    <Toggle checked disabled />
                  ) : (
                    <Toggle
                      checked={prefs[cat.key]}
                      onChange={(v) => setPrefs((p) => ({ ...p, [cat.key]: v }))}
                    />
                  )}
                </div>
              ))}
            </div>

            <div className="mt-6 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={cancelModal}
                className="px-4 py-2 text-sm rounded-lg text-gray-600 hover:bg-gray-50 transition"
              >
                Anuluj
              </button>
              <button
                type="button"
                onClick={() => finish({ analytics: false, ads: false })}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition"
              >
                Odrzuć wszystkie
              </button>
              <button
                type="button"
                onClick={() => finish(prefs)}
                className="px-4 py-2 text-sm rounded-lg bg-brand text-white font-semibold hover:bg-brand-600 transition"
              >
                Zapisz preferencje
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
